import React from 'react';
import { cn } from './cn';

/**
 * Page scaffold.
 *
 * `Page` is the scroll container the app-shell contract requires: #root is a
 * fixed, non-scrolling viewport, so a page that overflows without one of these
 * gets clipped rather than scrolled.
 *
 * It also owns the responsive gutters and the bottom safe-area inset, so no
 * page has to remember either. The `max()` on the horizontal padding is what
 * keeps content clear of a notch in landscape.
 */
export const Page = ({ className, children, ...props }) => (
  <div
    className={cn(
      'h-full overflow-y-auto overflow-x-hidden overscroll-y-contain bg-canvas',
      'pt-3 sm:pt-7 lg:pt-9',
      'pb-[calc(2rem+var(--safe-bottom))] sm:pb-[calc(2.5rem+var(--safe-bottom))] lg:pb-[calc(3.5rem+var(--safe-bottom))]',
      'px-[max(1rem,var(--safe-left))] sm:px-[max(1.5rem,var(--safe-left))] lg:px-[max(2.5rem,var(--safe-left))]',
      className
    )}
    // -webkit-overflow-scrolling has no Tailwind utility and is what gives iOS
    // momentum scrolling inside the shell.
    style={{ WebkitOverflowScrolling: 'touch' }}
    {...props}
  >
    {children}
  </div>
);

export const Container = ({ narrow, className, ...props }) => (
  <div
    className={cn('w-full mx-auto', narrow ? 'max-w-[760px]' : 'max-w-[1320px]', className)}
    {...props}
  />
);

/**
 * Page header.
 *
 * On a phone this is a single row — title on the left, actions on the right —
 * rather than a stack. The stacked version cost ~100px before any content, on
 * a screen whose entire job is showing content. From `sm` up there is room to
 * breathe, so it spreads out.
 */
export const PageHeader = ({ className, ...props }) => (
  <header
    className={cn(
      'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4',
      'sm:items-end sm:gap-6 sm:mb-9',
      className
    )}
    {...props}
  />
);

/**
 * Small uppercase label above a title.
 *
 * Hidden below `sm`. It names the section the title already names, and on a
 * phone that duplication costs a line of screen that a post could have used.
 * Pass `alwaysShow` where the eyebrow carries something the title does not.
 */
export const Eyebrow = ({ alwaysShow, className, ...props }) => (
  <p
    className={cn(
      'items-center gap-[7px] mb-2.5 text-eyebrow uppercase text-ink-muted',
      '[&>svg]:size-3.5 [&>svg]:shrink-0',
      alwaysShow ? 'flex' : 'hidden sm:flex',
      className
    )}
    {...props}
  />
);

/** Page title. Smaller on a phone — the clamp alone still left it dominating. */
export const Title = ({ as: Tag = 'h1', className, ...props }) => (
  <Tag className={cn('text-h5 sm:text-h3 text-ink', className)} {...props} />
);

export const SectionTitle = ({ as: Tag = 'h2', className, ...props }) => (
  <Tag className={cn('text-h5 text-ink', className)} {...props} />
);

/**
 * Explanatory line under a title.
 *
 * Hidden below `sm` by default. These sentences restate the title for someone
 * seeing the page for the first time, and they are read once and then cost a
 * line of screen forever. Pass `alwaysShow` when it carries real information.
 */
export const Subtitle = ({ alwaysShow, className, ...props }) => (
  <p
    className={cn(
      'mt-2 max-w-[62ch] text-body-sm text-ink-muted',
      alwaysShow ? 'block' : 'hidden sm:block',
      className
    )}
    {...props}
  />
);

export default Page;
